import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, IconButton, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import PrintIcon from '@mui/icons-material/Print';
import { usePrintDocument } from '../hooks/usePrintDocument';

function InfoTable({ title, rows }) {
  const filtered = rows.filter(r => r.value);
  if (!filtered.length) return null;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <div style={{ width: 3, height: 12, backgroundColor: '#FF6B00',
          borderRadius: 2 }} />
        <span style={{
          fontWeight: 700, fontSize: '0.62rem', color: '#FF6B00',
          letterSpacing: 1.5, textTransform: 'uppercase'
        }}>
          {title}
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {filtered.map((row, i) => (
            <tr key={i} style={{
              backgroundColor: i % 2 === 0
                ? 'rgba(255,107,0,0.04)' : 'transparent'
            }}>
              <td style={{
                fontSize: '0.68rem', color: '#999', padding: '3px 6px',
                width: 110, verticalAlign: 'top', whiteSpace: 'nowrap'
              }}>
                {row.label}
              </td>
              <td style={{
                fontSize: '0.68rem', fontWeight: 600, color: '#1a1a1a',
                padding: '3px 6px', verticalAlign: 'top',
                wordBreak: 'break-word'
              }}>
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
      <div style={{ width: 3, height: 13, backgroundColor: '#FF6B00',
        borderRadius: 2, flexShrink: 0 }} />
      <span style={{
        fontWeight: 700, fontSize: '0.7rem', color: '#FF6B00',
        letterSpacing: 1.5, textTransform: 'uppercase'
      }}>
        {children}
      </span>
    </div>
  );
}

export default function ReferenceProjectPreview() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);
  const handlePrint = usePrintDocument(printRef, {
    title: project?.name || 'Referensuppdrag',
    margin: '0',
  });

  useEffect(() => {
    async function load() {
      const snap = await getDoc(doc(db, 'referenceProjects', projectId));
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setProject(data);
        if (data.companyId) {
          const compSnap = await getDoc(doc(db, 'companies', data.companyId));
          if (compSnap.exists()) setCompany(compSnap.data());
        }
      }
      setLoading(false);
    }
    load();
  }, [projectId]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (!project) return (
    <Box sx={{ p: 4 }}>
      <Typography>Referensuppdraget hittades inte.</Typography>
      <Button onClick={() => navigate('/reference-projects')} sx={{ mt: 2 }}>
        Tillbaka
      </Button>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#e8e8e8' }}>

      {/* Toolbar */}
      <Box className="no-print" sx={{
        bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small"
            onClick={() => navigate('/reference-projects')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight="bold">
            {project.name} – Forhandsvisning
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ bgcolor: 'white', color: '#1a1a1a',
            '&:hover': { bgcolor: '#e0e0e0' } }}>
          Skriv ut / Spara som PDF
        </Button>
      </Box>

      {/* A4-wrapper */}
      <Box className="no-print-wrapper" sx={{
        maxWidth: 794, mx: 'auto', py: 4,
        '@media print': { py: 0, maxWidth: '100%' }
      }}>
        <div id="ref-content" ref={printRef} style={{
          backgroundColor: 'white',
          fontFamily: '"Segoe UI", Arial, sans-serif',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          borderRadius: 8,
          overflow: 'hidden'
        }}>

          {/* SIDHUVUD */}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '20px 32px 16px',
            borderBottom: '3px solid #1a1a1a',
            breakInside: 'avoid',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '0.6rem', fontWeight: 700, letterSpacing: 2,
                color: '#FF6B00', textTransform: 'uppercase', marginBottom: 3
              }}>
                Referensuppdrag
              </div>
              <div style={{
                fontSize: '1.4rem', fontWeight: 900, color: '#1a1a1a',
                lineHeight: 1.1, marginBottom: 3
              }}>
                {project.name}
              </div>
              {project.role && (
                <div style={{
                  fontSize: '0.85rem', color: '#555', fontWeight: 400
                }}>
                  {project.role}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 24 }}>
              {company?.logo ? (
                <img src={company.logo} alt="logo"
                  style={{
                    height: 36, objectFit: 'contain',
                    display: 'block', marginLeft: 'auto', marginBottom: 3
                  }} />
              ) : (
                <div style={{
                  fontSize: '1.1rem', fontWeight: 900, color: '#1a1a1a'
                }}>
                  Lagbas<span style={{ color: '#FF6B00' }}>N</span>
                </div>
              )}
              {project.projectNumber && (
                <div style={{ fontSize: '0.6rem', color: '#aaa', marginTop: 2 }}>
                  Nr: {project.projectNumber}
                </div>
              )}
            </div>
          </div>

          {/* BILD VÄNSTER 50% + REFERENSPERSON HÖGER 50% */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #e0e0e0',
            breakInside: 'avoid',
          }}>
            {project.image && (
              <div style={{
                width: '50%', flexShrink: 0,
                borderRight: '1px solid #e0e0e0',
                backgroundColor: '#f5f5f5',
                height: 220, overflow: 'hidden'
              }}>
                <img src={project.image} alt={project.name}
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: 'center',
                    display: 'block',
                  }} />
              </div>
            )}
            <div style={{
              width: project.image ? '50%' : '100%',
              flexShrink: 0,
              padding: '14px 18px',
            }}>
              <InfoTable
                title="Referensperson hos bestallare"
                rows={[
                  { label: 'Uppdragsgivare', value: project.client },
                  { label: 'Org.nr', value: project.clientOrgNumber },
                  { label: 'Namn', value: project.contactName },
                  { label: 'Befattning', value: project.contactRole },
                  { label: 'E-post', value: project.contactEmail },
                  { label: 'Telefon', value: project.contactPhone },
                ]}
              />
            </div>
          </div>

          {/* UPPGIFTER KRING UPPDRAG – full bredd */}
          <div style={{
            padding: '14px 32px',
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e0e0e0',
            breakInside: 'avoid',
          }}>
            <InfoTable
              title="Uppgifter kring uppdrag"
              rows={[
                { label: 'Projekt', value: project.name },
                { label: 'Budget', value: project.budget },
                { label: 'Start', value: project.startDate },
                { label: 'Slut', value: project.endDate },
                { label: 'Roll', value: project.role },
                { label: 'Konsult', value: project.consultantName },
                { label: 'Typ', value: project.projectType },
              ]}
            />
          </div>

          {/* BESKRIVNING */}
          <div style={{ padding: '20px 32px 24px' }}>

            {project.projectDescription && (
              <div style={{ marginBottom: 16 }}>
                <SectionHeading>Beskrivning av uppdrag</SectionHeading>
                <p style={{
                  fontSize: '0.83rem', lineHeight: 1.8,
                  color: '#333', margin: 0, paddingLeft: 10
                }}>
                  {project.projectDescription}
                </p>
              </div>
            )}

            {project.ourAssignment && (
              <div style={{ marginBottom: 16 }}>
                <SectionHeading>Vart uppdrag</SectionHeading>
                <p style={{
                  fontSize: '0.83rem', lineHeight: 1.8,
                  color: '#333', margin: 0, paddingLeft: 10
                }}>
                  {project.ourAssignment}
                </p>
              </div>
            )}

            {project.taskList?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <SectionHeading>Uppgifter</SectionHeading>
                <div style={{ paddingLeft: 10 }}>
                  {project.taskList.map((task, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: 7, marginBottom: 4
                    }}>
                      <span style={{
                        color: '#FF6B00', fontSize: '0.83rem',
                        flexShrink: 0, lineHeight: 1.7
                      }}>•</span>
                      <span style={{
                        fontSize: '0.83rem', lineHeight: 1.7, color: '#333'
                      }}>
                        {task}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(project.keywords?.length > 0 ||
              project.standards?.length > 0) && (
              <div style={{
                display: 'flex', gap: 32, flexWrap: 'wrap',
                paddingTop: 14, marginTop: 6,
                borderTop: '1px solid #f0f0f0',
              }}>
                {project.keywords?.length > 0 && (
                  <div>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: 4, marginBottom: 5
                    }}>
                      <div style={{
                        width: 3, height: 10,
                        backgroundColor: '#ccc', borderRadius: 2
                      }} />
                      <span style={{
                        fontWeight: 700, fontSize: '0.6rem', color: '#999',
                        letterSpacing: 1.5, textTransform: 'uppercase'
                      }}>
                        Nyckelord
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {project.keywords.map((k, i) => (
                        <span key={i} style={{
                          fontSize: '0.62rem', padding: '2px 7px',
                          border: '1px solid #ddd', borderRadius: 3,
                          color: '#666', display: 'inline-block'
                        }}>
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {project.standards?.length > 0 && (
                  <div>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: 4, marginBottom: 5
                    }}>
                      <div style={{
                        width: 3, height: 10,
                        backgroundColor: '#FF6B00', borderRadius: 2
                      }} />
                      <span style={{
                        fontWeight: 700, fontSize: '0.6rem', color: '#FF6B00',
                        letterSpacing: 1.5, textTransform: 'uppercase'
                      }}>
                        Standarder
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {project.standards.map((s, i) => (
                        <span key={i} style={{
                          fontSize: '0.62rem', padding: '2px 7px',
                          border: '1px solid #FF6B00', borderRadius: 3,
                          color: '#FF6B00', display: 'inline-block'
                        }}>
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SIDFOT */}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', padding: '8px 32px',
            backgroundColor: '#1a1a1a'
          }}>
            <span style={{ fontSize: '0.6rem', color: '#777' }}>
              {[company?.name, company?.address].filter(Boolean).join(' | ')}
            </span>
            {company?.logo && (
              <img src={company.logo} alt="logo"
                style={{
                  height: 16, objectFit: 'contain',
                  filter: 'brightness(0) invert(1)', opacity: 0.4
                }} />
            )}
            <span style={{ fontSize: '0.6rem', color: '#777' }}>
              {[company?.phone, company?.website, company?.email]
                .filter(Boolean).join(' | ')}
            </span>
          </div>

        </div>
      </Box>

      {/* Print CSS */}
      <style>{`
        .no-print { display: flex; }

        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print {
            display: none !important;
          }
          .no-print-wrapper {
            padding: 0 !important;
            max-width: 100% !important;
            margin: 0 !important;
          }
          #ref-content {
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          p {
            orphans: 3;
            widows: 3;
          }
          h1, h2, h3 {
            break-after: avoid;
            page-break-after: avoid;
          }
        }
      `}</style>
    </Box>
  );
}